/**
 * PublishingCardCH — right-rail publishing summary for a Challenge.
 *
 *   • Type    — "Free" / "Premium" pill (from challenge_is_free)
 *   • Author  — resolved name from the authors slice
 *   • Goal    — challenge_goal (text)
 *   • Duration — `${total_days} ${challenge_day_type}s` ("30 DAYs")
 *   • Sessions — count of sortable session rows
 *
 * Plus the canonical Activate / Deactivate link in the footer.
 */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

export default function PublishingCardCH({
  state,
  sessionCount,
  onToggleActive,
}) {
  const isActive = !!state.challenge_is_active;
  const isFree = !!state.challenge_is_free;

  const authors = useSelector((s) => s.authors?.authors) || [];
  const authorName = useMemo(() => {
    if (!state.challenge_author_id) return null;
    const hit = authors.find(
      (a) => Number(a.author_id) === Number(state.challenge_author_id),
    );
    return hit?.author_name || null;
  }, [authors, state.challenge_author_id]);

  const dayType = state.challenge_day_type || 'DAY';
  const durationLabel = state.total_days
    ? `${state.total_days} ${dayType.toLowerCase()}${Number(state.total_days) === 1 ? '' : 's'}`
    : '—';

  return (
    <RailCard
      variant="publish"
      head={({ open, toggle }) => (
        <RailCardHead
          title="Publishing"
          variant="publish"
          open={open}
          onToggle={toggle}
          pill={
            <span
              className="font-condensed font-bold uppercase"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '3px 9px',
                borderRadius: 999,
                background: isActive ? '#75A29333' : '#9CA1A81F',
                color: isActive ? '#5C7E72' : '#9CA1A8',
                whiteSpace: 'nowrap',
              }}
            >
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
      )}
    >
      <div style={{ padding: '14px 18px' }}>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px 14px',
            margin: 0,
          }}
        >
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Access
            </dt>
            <dd style={{ margin: '4px 0 0' }}>
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: isFree ? '#75A29333' : '#E58A6A',
                  color: isFree ? '#5C7E72' : '#FFFFFF',
                }}
              >
                {isFree ? 'Free' : 'Premium'}
              </span>
            </dd>
          </div>
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Duration
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {durationLabel}
            </dd>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Author
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {authorName || '—'}
            </dd>
          </div>
          {state.challenge_goal && (
            <div style={{ gridColumn: '1 / -1' }}>
              <dt
                className="font-condensed font-bold uppercase text-text-muted"
                style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
              >
                Goal
              </dt>
              <dd
                className="text-navy"
                style={{
                  margin: '2px 0 0',
                  fontSize: 12.5,
                  fontWeight: 500,
                  fontStyle: 'italic',
                }}
              >
                {state.challenge_goal}
              </dd>
            </div>
          )}
          <div>
            <dt
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 10, letterSpacing: '0.08em', margin: 0 }}
            >
              Sessions
            </dt>
            <dd
              className="text-navy"
              style={{ margin: '2px 0 0', fontSize: 12.5, fontWeight: 500 }}
            >
              {sessionCount ?? 0}
            </dd>
          </div>
        </dl>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #ECE3D2',
            textAlign: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => onToggleActive?.(!isActive)}
            className="font-semibold cursor-pointer"
            style={{
              fontSize: 12,
              color: isActive ? '#C4715B' : '#5C7E72',
              background: 'transparent',
              border: 0,
              padding: 0,
            }}
          >
            {isActive ? 'Deactivate challenge' : 'Activate challenge'}
          </button>
        </div>
      </div>
    </RailCard>
  );
}
